import type { FeatureLike } from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import { getLength } from 'ol/sphere';
import {
  Circle as CircleStyle,
  Fill,
  RegularShape,
  Stroke,
  Style,
  Text,
} from 'ol/style';

const style: Style = new Style({
  fill: new Fill({
    color: 'rgba(60,129,255,0.81)',
  }),
  stroke: new Stroke({
    color: 'rgba(70,125,255,0.87)',
    lineDash: [10, 10],
    width: 2,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(75,255,188,0.73)',
    }),
    fill: new Fill({
      color: 'rgba(118,255,206,0.7)',
    }),
  }),
});

const labelStyle: Style = new Style({
  text: new Text({
    font: '14px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)',
    }),
    backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.7)',
    }),
    padding: [3, 3, 3, 3],
    textBaseline: 'bottom',
    offsetY: -15,
  }),
  image: new RegularShape({
    radius: 8,
    points: 3,
    angle: Math.PI,
    displacement: [0, 10],
    fill: new Fill({
      color: 'rgba(0, 0, 0, 0.7)',
    }),
  }),
});

export const rulerModifyStyle: Style = new Style({
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(255,224,30,0.91)',
    }),
    fill: new Fill({
      color: 'rgba(255,176,65,0.91)',
    }),
  }),
  text: new Text({
    text: 'Drag to modify',
    font: '12px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)',
    }),
    backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.7)',
    }),
    padding: [2, 2, 2, 2],
    textAlign: 'left',
    offsetX: 15,
  }),
});

const segmentStyleTemplate: Style = new Style({
  text: new Text({
    font: '12px Calibri,sans-serif',
    fill: new Fill({
      color: 'rgba(255, 255, 255, 1)',
    }),
    backgroundFill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)',
    }),
    padding: [2, 2, 2, 2],
    textBaseline: 'bottom',
    offsetY: -12,
  }),
  image: new RegularShape({
    radius: 6,
    points: 3,
    angle: Math.PI,
    displacement: [0, 8],
    fill: new Fill({
      color: 'rgba(0, 0, 0, 0.4)',
    }),
  }),
});

const segmentStyles: Style[] = [segmentStyleTemplate];

export function formatLength(line: LineString): string {
  const length = getLength(line);
  if (length > 1000) {
    return `${Math.round((length / 1000) * 100) / 100} km`;
  }

  return `${Math.round(length * 100) / 100} m`;
}

export function rulerStyleFunction(feature: FeatureLike): Style[] {
  const styles: Style[] = [];
  const geometry = feature.getGeometry();
  if (!geometry) {
    return styles;
  }

  const type = geometry.getType();
  const point: Point = new Point((geometry as LineString).getLastCoordinate());
  const label = formatLength(geometry as LineString);
  const line = geometry as LineString;
  styles.push(style);

  if (type === 'LineString') {
    let segmentIndex = 0;
    line.forEachSegment((startPoint: number[], endPoint: number[]) => {
      const segment = new LineString([startPoint, endPoint]);
      const label = formatLength(segment);
      if (segmentStyles.length - 1 < segmentIndex) {
        segmentStyles.push(segmentStyleTemplate.clone());
      }
      const segmentPoint = new Point(segment.getCoordinateAt(0.5));
      const segmentStyle = segmentStyles[segmentIndex];
      segmentStyle.setGeometry(segmentPoint);
      segmentStyle.getText()?.setText(label);
      styles.push(segmentStyle);
      segmentIndex++;
    });
  }

  if (label !== '') {
    labelStyle.setGeometry(point);
    labelStyle.getText()?.setText(label);
    styles.push(labelStyle);
  }

  return styles;
}
